import { ERROR_MESSAGES, LIMITS, type FileDeclaration } from '@onerepair/contract';
import { fmtBytes, uid } from '../lib/format.ts';
import { looksLikePdf } from '../lib/pdf.ts';
import { suggestDomain } from '../lib/email.ts';
import { track } from '../lib/telemetry.ts';
import { ApiRequestError, api } from './api.ts';
import { canAdd, initialState, reduce, submitBlocker, type Action, type IntakeFile, type IntakeState } from './state.ts';
import type { UploadHandle } from './uploader.ts';

const q = <T extends HTMLElement>(root: ParentNode, name: string) => root.querySelector<T>(`[data-el="${name}"]`)!;

export function mountIntake(form: HTMLFormElement): void {
  const dropzone = q<HTMLDivElement>(form, 'dropzone'), empty = q<HTMLDivElement>(form, 'empty'), filelist = q<HTMLDivElement>(form, 'filelist');
  const items = q<HTMLUListElement>(form, 'items'), listHint = q<HTMLDivElement>(form, 'listHint');
  const fileInput = q<HTMLInputElement>(form, 'fileInput'), email = q<HTMLInputElement>(form, 'email'), honeypot = q<HTMLInputElement>(form, 'honeypot');
  const submit = q<HTMLButtonElement>(form, 'submit'), error = q<HTMLDivElement>(form, 'error'), live = q<HTMLDivElement>(form, 'live');
  const body = q<HTMLDivElement>(form, 'body'), done = q<HTMLDivElement>(form, 'done');
  const doneCount = q<HTMLSpanElement>(form, 'doneCount'), doneNoun = q<HTMLSpanElement>(form, 'doneNoun'), doneEmail = q<HTMLElement>(form, 'doneEmail'), reset = q<HTMLButtonElement>(form, 'reset');
  const tpl = document.getElementById('tpl-file-row') as HTMLTemplateElement;

  let state: IntakeState = initialState();
  let token = '';
  const blobs = new Map<string, File>();
  const uploads = new Map<string, UploadHandle>();
  const rows = new Map<string, HTMLLIElement>();

  const dispatch = (a: Action) => { state = reduce(state, a); render(); };

  function render() {
    const has = state.files.length > 0;
    empty.hidden = has; filelist.hidden = !has;
    form.classList.toggle('has-files', has); // the email row appears once a manual is in
    for (const f of state.files) renderRow(f);
    for (const [id, li] of rows) if (!state.files.some((f) => f.clientId === id)) { li.remove(); rows.delete(id); }
    const uploaded = state.files.filter((f) => f.status === 'uploaded').length;
    listHint.textContent = has ? `${uploaded} of ${state.files.length} uploaded. Drop more, or add your email below.` : '';
    error.hidden = !state.error; error.textContent = state.error;
    submit.disabled = state.submitting; submit.classList.toggle('disabled', !!submitBlocker(state));
    body.hidden = state.submitted; done.hidden = !state.submitted;
    if (state.submitted) { doneCount.textContent = String(state.files.length); doneNoun.textContent = state.files.length === 1 ? 'manual' : 'manuals'; doneEmail.textContent = state.email.trim(); }
  }

  function renderRow(f: IntakeFile) {
    let li = rows.get(f.clientId);
    if (!li) {
      li = (tpl.content.firstElementChild as HTMLLIElement).cloneNode(true) as HTMLLIElement;
      li.dataset.fileId = f.clientId;
      q<HTMLElement>(li, 'name').textContent = f.name;
      q<HTMLElement>(li, 'size').textContent = fmtBytes(f.sizeBytes);
      q<HTMLButtonElement>(li, 'remove').addEventListener('click', () => removeFile(f.clientId));
      q<HTMLButtonElement>(li, 'retry').addEventListener('click', () => retryFile(f.clientId));
      items.appendChild(li); rows.set(f.clientId, li);
    }
    const status = q<HTMLElement>(li, 'status'), label = q<HTMLElement>(li, 'statusLabel'), retry = q<HTMLButtonElement>(li, 'retry');
    li.classList.toggle('failed', f.status === 'failed');
    status.classList.toggle('processing', f.status === 'uploading' || f.status === 'queued');
    status.classList.toggle('ready', f.status === 'uploaded');
    label.textContent = f.status === 'uploaded' ? 'Uploaded' : f.status === 'failed' ? 'Failed' : f.status === 'queued' ? 'Waiting' : `Uploading ${Math.round(f.progress * 100)}%`;
    retry.hidden = f.status !== 'failed';
    li.title = f.error ?? '';
  }

  async function addFiles(list: FileList | File[]) {
    if (state.submitted) return;
    const files = Array.from(list);
    const limit = canAdd(state, files.length);
    if (limit) return dispatch({ type: 'error', error: ERROR_MESSAGES[limit] });
    const accepted: IntakeFile[] = [];
    for (const file of files) {
      if (!(await looksLikePdf(file))) { dispatch({ type: 'error', error: `${file.name}: ${ERROR_MESSAGES.NOT_PDF}` }); continue; }
      if (file.size > LIMITS.maxFileBytes) { dispatch({ type: 'error', error: `${file.name}: ${ERROR_MESSAGES.FILE_TOO_LARGE}` }); continue; }
      if (file.size < LIMITS.minFileBytes) { dispatch({ type: 'error', error: `${file.name}: ${ERROR_MESSAGES.FILE_TOO_SMALL}` }); continue; }
      const clientId = uid();
      blobs.set(clientId, file);
      accepted.push({ clientId, name: file.name, sizeBytes: file.size, progress: 0, status: 'queued' });
    }
    if (!accepted.length) return;
    dispatch({ type: 'add', files: accepted });
    track('intake_files_added', { count: accepted.length, totalBytes: accepted.reduce((a, f) => a + f.sizeBytes, 0) });
    await issueAndUpload(accepted);
  }

  async function issueAndUpload(files: IntakeFile[]) {
    const decls: FileDeclaration[] = files.map((f) => ({ clientId: f.clientId, name: f.name, sizeBytes: f.sizeBytes, contentType: 'application/pdf' }));
    try {
      let issued;
      if (!state.submissionId) {
        const res = await api.create(decls, await turnstileToken(), honeypot.value);
        token = res.submissionToken; dispatch({ type: 'submission', submissionId: res.submissionId }); issued = res.files;
      } else {
        issued = (await api.addFiles(state.submissionId, token, decls)).files;
      }
      for (const i of issued) { dispatch({ type: 'issued', clientId: i.clientId, fileId: i.fileId }); void upload(i.clientId, i.fileId, i.uploadUrl); }
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : ERROR_MESSAGES.INTERNAL;
      for (const f of files) dispatch({ type: 'failed', clientId: f.clientId, error: msg });
      dispatch({ type: 'error', error: msg });
    }
  }

  async function upload(clientId: string, fileId: string, uploadUrl: string) {
    const file = blobs.get(clientId); if (!file || !state.submissionId) return;
    const { startUpload } = await import('./uploader.ts');
    const started = performance.now();
    const handle = startUpload({
      file, uploadUrl,
      renew: async () => { track('upload_sas_renewed'); return (await api.renewSas(state.submissionId!, token, fileId)).uploadUrl; },
      onProgress: (p) => dispatch({ type: 'progress', clientId, progress: p }),
      metadata: { submissionid: state.submissionId, fileid: fileId, originalname: encodeURIComponent(file.name), declaredsize: String(file.size) },
    });
    uploads.set(clientId, handle);
    track('upload_started', { bytes: file.size });
    try {
      await handle.promise;
      dispatch({ type: 'uploaded', clientId });
      track('upload_completed', { bytes: file.size, durationMs: Math.round(performance.now() - started) });
      live.textContent = `${file.name} uploaded.`;
    } catch (e) {
      if (!state.files.some((f) => f.clientId === clientId)) return; // removed while uploading
      dispatch({ type: 'failed', clientId, error: (e as Error).message });
      track('upload_failed', { code: String((e as { statusCode?: number }).statusCode ?? 'network') });
    } finally {
      uploads.delete(clientId);
    }
  }

  async function removeFile(clientId: string) {
    const f = state.files.find((x) => x.clientId === clientId);
    uploads.get(clientId)?.abort();
    track('file_removed', { status: f?.status ?? 'unknown' });
    dispatch({ type: 'remove', clientId }); blobs.delete(clientId);
    if (f?.fileId && state.submissionId) await api.removeFile(state.submissionId, token, f.fileId).catch(() => undefined);
  }

  async function retryFile(clientId: string) {
    const f = state.files.find((x) => x.clientId === clientId); if (!f || !state.submissionId) return;
    dispatch({ type: 'retry', clientId });
    if (f.fileId) {
      try { const sas = await api.renewSas(state.submissionId, token, f.fileId); dispatch({ type: 'issued', clientId, fileId: f.fileId }); void upload(clientId, f.fileId, sas.uploadUrl); }
      catch (e) { dispatch({ type: 'failed', clientId, error: (e as Error).message }); }
    } else await issueAndUpload([f]);
  }

  async function turnstileToken(): Promise<string | undefined> {
    const w = window as unknown as { turnstile?: { getResponse: (el?: Element) => string | undefined } };
    return w.turnstile?.getResponse(q(form, 'turnstile')) || undefined;
  }

  async function onSubmit(ev: Event) {
    ev.preventDefault();
    dispatch({ type: 'email', email: email.value });
    const blocker = submitBlocker(state);
    if (blocker) { track('submit_blocked', { reason: blocker }); dispatch({ type: 'error', error: blocker }); (blocker === ERROR_MESSAGES.INVALID_EMAIL ? email : submit).focus(); return; }
    const suggestion = suggestDomain(email.value);
    if (suggestion && !email.dataset.suggested) { track('email_typo_suggested'); email.dataset.suggested = '1'; dispatch({ type: 'error', error: `Did you mean ${email.value.trim().split('@')[0]}@${suggestion}? Press send again to keep it.` }); return; }
    dispatch({ type: 'submitting' });
    try {
      const res = await api.complete(state.submissionId!, token, email.value.trim());
      dispatch({ type: 'submitted' });
      track('submission_completed', { fileCount: res.fileCount, totalBytes: res.totalBytes, emailDomain: state.email.trim().split('@')[1]?.toLowerCase() ?? '' });
      live.textContent = 'Received. Your free demo is being built.';
    } catch (e) {
      if (e instanceof ApiRequestError && e.body.code === 'FILES_NOT_READY') {
        for (const p of (e.body.files as { fileId: string; code: string }[]) ?? []) {
          const f = state.files.find((x) => x.fileId === p.fileId);
          if (f) dispatch({ type: 'failed', clientId: f.clientId, error: p.code === 'NOT_PDF' ? ERROR_MESSAGES.NOT_PDF : p.code === 'MALWARE' ? 'This file was flagged by malware scanning and removed.' : 'Upload did not complete. Retry this file.' });
        }
      }
      dispatch({ type: 'error', error: e instanceof ApiRequestError ? e.message : ERROR_MESSAGES.INTERNAL });
      track('submission_failed', { code: e instanceof ApiRequestError ? e.body.code : 'network' });
    }
  }

  // wiring
  dropzone.addEventListener('dragover', (e) => { e.preventDefault(); form.classList.add('dragging'); });
  dropzone.addEventListener('dragleave', () => form.classList.remove('dragging'));
  dropzone.addEventListener('drop', (e) => { e.preventDefault(); form.classList.remove('dragging'); if (e.dataTransfer?.files) { track('intake_files_dropped', { count: e.dataTransfer.files.length }); void addFiles(e.dataTransfer.files); } });
  dropzone.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('button, a, label')) return; track('intake_picker_opened', { via: 'dropzone' }); fileInput.click(); });
  dropzone.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); track('intake_picker_opened', { via: 'keyboard' }); fileInput.click(); } });
  fileInput.addEventListener('change', () => { if (fileInput.files) void addFiles(fileInput.files); fileInput.value = ''; });
  email.addEventListener('input', () => { delete email.dataset.suggested; dispatch({ type: 'email', email: email.value }); });
  email.addEventListener('change', () => { if (email.value.includes('@') && !email.dataset.entered) { email.dataset.entered = '1'; track('email_entered'); } }, { passive: true });
  form.addEventListener('submit', (e) => void onSubmit(e));
  reset.addEventListener('click', () => { track('intake_reset'); token = ''; blobs.clear(); rows.forEach((li) => li.remove()); rows.clear(); email.value = ''; dispatch({ type: 'reset' }); });
  window.addEventListener('beforeunload', (e) => { if (uploads.size) { e.preventDefault(); e.returnValue = ''; } });
  render();
}
